<template>
  <div>
    <LocationKioskView />

    <ItemDetailsView />

    <PaymentOptionsView style="z-index: 1600" @startPayment="startPayment" />

    <PaymentMethodInputView v-if="showPaymentMethodInput" style="z-index: 1650" @startPayment="startPayment" />

    <PaymentPendingView v-if="awaitingPayment" @cancelPayment="cancelPayment" style="z-index: 1800" />

    <PaymentFailureView v-if="paymentFailed" style="z-index: 1801" />

    <OrderConfirmationPendingView v-if="orderConfirmationPending" style="z-index: 1802" />

    <OrderSuccessView v-if="paymentSuccess" style="z-index: 1805" @reset="reset" />    

    <UpsellView v-if="showUpsell" style="z-index: 1640" />

    <PayerInfoInputView style="z-index: 1651" />

    <CouponDialog style="z-index: 1660" />
  </div>
</template>

<script>
import * as Sentry from "@sentry/vue";
import { useStore, VERIFONE_NAMESPACE, VERIFONE_P_NAMESPACE } from '../stores/store';

import { VerifoneP } from '@noona-hq/com-salescloud-verifonep';
import { VerifoneVx } from '@noona-hq/com-salescloud-verifonevx';
import { defineAsyncComponent } from 'vue';
import { version } from '../../package.json';

// How long to wait for a reader to say anything at all. Vx has no connect timeout, so
// without this an offline reader leaves the kiosk on a pending screen indefinitely.
const CARD_READER_RESPONSE_TIMEOUT_MS = 60000;

const PaymentMethodInputView = defineAsyncComponent({
  loader: () => import('./PaymentMethodInputView.vue'),
})

const OrderSuccessView = defineAsyncComponent({
  loader: () => import('./OrderSuccessView.vue'),
})

const PaymentFailureView = defineAsyncComponent({
  loader: () => import('./PaymentFailureView.vue'),
})

const PaymentPendingView = defineAsyncComponent({
  loader: () => import('./PaymentPendingView.vue'),
})

const PaymentOptionsView = defineAsyncComponent({
  loader: () => import('./PaymentOptionsView.vue'),
})

const ItemDetailsView = defineAsyncComponent({
  loader: () => import('./ItemDetailsView.vue'),
})

const LocationKioskView = defineAsyncComponent({
  loader: () => import('./LocationKioskView.vue'),
})

const UpsellView = defineAsyncComponent({
  loader: () => import('./UpsellView.vue'),
})

const PayerInfoInputView = defineAsyncComponent({
  loader: () => import('./PayerInfoInputView.vue'),
})

const OrderConfirmationPendingView = defineAsyncComponent({
  loader: () => import('./OrderPendingConfirmationView.vue'),
})

const CouponDialog = defineAsyncComponent({
  loader: () => import('../components/coupon/CouponDialog.vue'),
})

export default {
  name: 'MainView',
  components: {
    PaymentMethodInputView,
    OrderSuccessView,
    PaymentFailureView,
    PaymentPendingView,
    PaymentOptionsView,
    ItemDetailsView,
    LocationKioskView,
    UpsellView,
    PayerInfoInputView,
    OrderConfirmationPendingView,
    CouponDialog,
  },
  setup() {
    const store = useStore()
    return {
      store,
    }
  },
  data() {
    return {
      timeoutId: null,
      cardReaderActionId: null,
      cardReaderMessageListener: null,
      verifonePMessageListener: null,
      verifonePCompletionListener: null,
      cardReaderWatchdogId: null,
    }
  },
  computed: {
    cartTotal() {
      return this.store.getCartTotal
    },
    kiosk() {
      return this.store.kiosk
    },
    maintenanceMode() {
      if (typeof this.kiosk !== 'undefined' && this.kiosk !== null) {
        return this.kiosk.maintenanceMode
      }
      return false
    },
    version() {
      return version
    },
    gettingInitialData() {
      return this.store.gettingInitialData
    },
    organization() {
      if (typeof this.kiosk !== 'undefined' && this.kiosk !== null) {
        return this.kiosk.organization
      }
      return null
    },
    order() {
      return this.store.order
    },
    selectedLocation() {
      if (this.store.order) {
        return this.store.order.selectedLocation
      }
      return null
    },
    selectedLocationPackagingType() {
      if (this.order) {
        return this.order.selectedLocationPackagingType
      }
      return null
    },
    showPaymentOptions() {
      return this.store.showPaymentOptions
    },
    awaitingPayment() {
      return this.store.awaitingPayment
    },
    paymentResponse() {
      return this.store.paymentResponse
    },
    paymentSuccess() {
      return this.store.paymentSuccess
    },
    orderConfirmationPending() {
      return this.store.orderConfirmationPending
    },
    paymentFailed() {
      return this.store.paymentFailed
    },
    showPackagingOptions() {
      return this.store.showPackagingOptions
    },
    showPaymentMethodInput() {
      return this.store.showPaymentMethodInput
    },
    showUpsell() {
      return this.store.showUpsell
    },
    showPayerInfoInput() {
      return this.store.showPayerInfoInput
    },
    checkoutScreensShowing() {
      return (
        this.showPaymentOptions ||
        this.showUpsell ||
        this.showPayerInfoInput ||
        this.showPaymentMethodInput ||
        this.awaitingPayment ||
        this.paymentFailed ||
        this.orderConfirmationPending ||
        this.paymentSuccess
      )
    },
  },
  methods: {
    refresh() {
      this.store.getKioskData(this.store.kioskUUID)
    },
    closePaymentOptions() {
      this.store.showPaymentOptions = false
    },
    cancelPayment() {
      this.cancelCardReaderTransaction(this.order?.selectedPaymentMethod?.namespace)

      // Deliberately leaves cardReaderTransactionInFlight to the plugin's failure
      // message: a cancel can fail, and unfreezing the cart while the terminal is
      // still live on the original amount would reopen HQ-9834.
    },
    reset() {
      this.store.reset()
    },
    closePaymentFailed() {
      // Clearing paymentResponse removes the resolved status keeping awaitingPayment
      // false, so the flag has to go with it
      this.clearCardReaderWatchdog()
      this.store.endCardReaderTransaction()
      this.store.paymentResponse = null
      this.store.originalPaymentResponse = null
      this.store.showPaymentOptions = true
    },
    startPayment() {
      this.store.customLogger('info', 'Start Payment Called on MainView', this.order)

      if (
        this.order !== null &&
        this.order !== undefined &&
        this.order.selectedPaymentMethod !== null &&
        this.order.selectedPaymentMethod !== undefined &&
        this.order.selectedPaymentMethod.namespace !== null &&
        this.order.selectedPaymentMethod.namespace !== undefined
      ) {
        switch (this.order.selectedPaymentMethod.namespace) {
          case VERIFONE_NAMESPACE:
            this.startVerifonePayment()
            break
          case VERIFONE_P_NAMESPACE:
            this.startVerifonePPayment()
            break
          default:
            this.startWebPayment()
            break
        }
      }
    },
    startWebPayment() {
      const paymentRequest = this.store.getPaymentRequest(null, this.store.getCartTotal)
      this.store.processPayment(paymentRequest)
    },
    clearCardReaderWatchdog() {
      if (this.cardReaderWatchdogId) {
        clearTimeout(this.cardReaderWatchdogId)
        this.cardReaderWatchdogId = null
      }
    },
    cancelCardReaderTransaction(namespace) {
      try {
        switch (namespace) {
          case VERIFONE_P_NAMESPACE:
            VerifoneP.cancelTransaction()
            break
          case VERIFONE_NAMESPACE:
            VerifoneVx.cancelTransaction()
            break
        }
      } catch (error) {
        Sentry.captureException(error, { extra: 'cancelCardReaderTransaction' })
      }
    },
    /**
     * @description The single way a card reader attempt ends badly on our side, used
     * by the watchdog and by a rejected start. Routes to PaymentFailureView, which the
     * customer can dismiss back to the payment options. Posts no payment request - the
     * paymentResponse watcher only acts on 'success' and 'pending'.
     */
    failCardReaderTransaction(namespace, currencyCode, message) {
      this.clearCardReaderWatchdog()
      this.store.endCardReaderTransaction()
      this.store.paymentResponse = {
        paymentTransaction: {
          namespace,
          currency_code: currencyCode,
          status: 'failure',
          message,
          payload: {},
        },
      }
    },
    /**
     * @description Fails the attempt if the reader never answers, so an unattended
     * kiosk recovers on its own. Keyed on "no response yet" rather than "not
     * finished": any response voids it, so it cannot cut short a customer who is
     * simply slow at the terminal.
     */
    armCardReaderWatchdog(namespace, currencyCode) {
      this.clearCardReaderWatchdog()

      this.cardReaderWatchdogId = setTimeout(() => {
        this.cardReaderWatchdogId = null

        if (!this.store.cardReaderTransactionInFlight) {
          return
        }

        if (this.store.paymentResponse?.paymentTransaction) {
          return
        }

        this.store.customLogger('error', 'Card reader never responded, failing the payment attempt', {
          namespace,
          timeoutMs: CARD_READER_RESPONSE_TIMEOUT_MS,
        })
        Sentry.captureMessage('Card reader never responded to a started transaction', {
          level: 'warning',
          extra: { namespace, timeoutMs: CARD_READER_RESPONSE_TIMEOUT_MS },
        })

        // Cancel before giving up, so the reader is not left live on an amount the
        // customer could still pay against a cart we have now unfrozen
        this.cancelCardReaderTransaction(namespace)
        this.failCardReaderTransaction(namespace, currencyCode, 'Failed to connect to the card reader')
      }, CARD_READER_RESPONSE_TIMEOUT_MS)
    },
    /**
     * @description A message arriving with no attempt in flight belongs to one we
     * already abandoned, so it must not repopulate paymentResponse and put the
     * payment screens back over whatever the kiosk is doing now.
     */
    ignoreStaleCardReaderResponse(payload) {
      if (this.store.cardReaderTransactionInFlight) {
        return false
      }
      this.store.customLogger('error', 'Ignoring card reader response for an abandoned attempt')
      Sentry.captureMessage('Card reader response arrived with no attempt in flight; ignoring', {
        level: 'error',
        extra: { payload },
      })
      return true
    },
    /**
     * Handle verifone payment response
     * @param data
     */
    logVerifoneResponse(data) {
      if (data?.message) {
        if (this.ignoreStaleCardReaderResponse(data.message)) {
          return
        }
        try {
          this.store.paymentResponse = JSON.parse(data.message)
          if (this.store.paymentResponse.paymentTransaction.status !== 'pending') {
            this.store.processCardReaderPaymentRequest(this.store.paymentResponse)
          }
        } catch (e) {
          console.log('could not parse data message', data.message)
        }
      }
    },
    /**
     * Start a verifone payment request
     * @return {Promise<void>}
     */
    startVerifonePayment() {
      this.store.latestCardReaderMessage = 'Starting card reader connection'
      this.store.customLogger('info', 'Starting Verifone Payment')

      const cardReaderPaymentMethodInstance = this.kiosk.cardReaderPaymentMethodInstance
      const cardReaderConfiguration = JSON.parse(cardReaderPaymentMethodInstance.data)

      let verifoneAmount = this.cartTotal.amount

      if (this.cartTotal.currency_code !== null && this.cartTotal.currency_code !== undefined && this.cartTotal.currency_code === 'ISK') {
        verifoneAmount = verifoneAmount * 100
      }

      const cardReaderTransactionRequest = {
        ipAddress: cardReaderConfiguration.ip_address,
        merchantId: cardReaderConfiguration.merchant_id,
        terminalNumber: cardReaderConfiguration.terminal,
        transactionType: 0,
        amount: verifoneAmount,
        currencyCode: this.cartTotal.currency_code,
      }

      this.latestCardReaderMessage = 'Connecting to card reader'
      this.store.customLogger('info', 'Connecting to card reader', cardReaderTransactionRequest)

      // Vx reports nothing until the reader answers, so raise the pending screen
      // ourselves. After the config and amount, so a throw above cannot strand it.
      this.store.beginCardReaderTransaction()
      const currencyCode = this.cartTotal.currency_code
      this.armCardReaderWatchdog(VERIFONE_NAMESPACE, currencyCode)

      // eslint-disable-next-line
      VerifoneVx.startTransaction(cardReaderTransactionRequest)
        .then(() => {
          this.store.customLogger('info', 'Card reader connection finished', this.store.paymentResponse)
        })
        .catch((error) => {
          // Never taken by the reader, so show the failure rather than dropping the
          // customer back on the catalog with nothing said
          this.failCardReaderTransaction(VERIFONE_NAMESPACE, currencyCode, 'Failed to connect to the card reader')
          this.store.customLogger('error', 'Error connecting to card reader', this.store.paymentResponse)
          Sentry.captureException(error, {extra: "startVerifonePayment: Error connecting to card reader"})
        })
    },
    /**
     * Cancel a verifone payment request
     * @return {Promise<void>}
     */
    async cancelVerifonePayment() {
      // const cardReaderTransactionRequest = {}

      await VerifoneVx.cancelTransaction()
        .then((cardReaderResponse) => {
          if (typeof cardReaderResponse !== 'undefined' && cardReaderResponse !== null && typeof cardReaderResponse.success === 'boolean') {
            if (cardReaderResponse.success === true) {
              //
            }
          }

          return cardReaderResponse
        })
        .catch(() => {
          //
        })
    },
    handleVerifonePResponse(data) {
      if (data?.paymentTransactionResponse) {
        if (this.ignoreStaleCardReaderResponse(data.paymentTransactionResponse)) {
          return
        }
        try {
          const newResponse = JSON.parse(data.paymentTransactionResponse)
          this.store.paymentResponse = newResponse
        } catch (e) {
          console.log('could not parse data message', e, data.paymentTransactionResponse)
          Sentry.captureException(e, { extra: "handleVerifonePResponse: Error parsing data response", data: data.paymentTransactionResponse })
        }
      }
    },
    /**
     * Handle verifone P payment response
     * @param data
     */
     handleVerifonePCompletion(data) {
      if (data?.paymentTransactionResponse) {
        if (this.ignoreStaleCardReaderResponse(data.paymentTransactionResponse)) {
          return
        }
        try {
          this.store.paymentResponse = JSON.parse(data.paymentTransactionResponse)

          this.store.processCardReaderPaymentRequest(this.store.paymentResponse)
        } catch (e) {
          console.log('could not parse data response', e, data.paymentTransactionResponse)
          Sentry.captureException(e, { extra: "handleVerifonePCompletion: Error parsing data response", data: data.paymentTransactionResponse })
        }
      }
    },
    /**
     * Start a verifone P payment request
     * @return {Promise<void>}
     */
    startVerifonePPayment() {
      this.store.paymentResponse = null

      this.store.latestCardReaderMessage = 'Starting card reader connection'

      const cardReaderPaymentMethodInstance = this.kiosk.cardReaderPaymentMethodInstance
      
      const cardReaderConfiguration = JSON.parse(cardReaderPaymentMethodInstance.data)

      let verifoneAmount = this.cartTotal.amount

      const cardReaderTransactionRequest = {
        ipAddress: cardReaderConfiguration.ip_address,
        merchantId: cardReaderConfiguration.merchant_id,
        terminalNumber: cardReaderConfiguration.terminal,
        serialNumber: cardReaderConfiguration.serial_number,
        amount: verifoneAmount,
        currencyCode: this.cartTotal.currency_code,
      }

      this.latestCardReaderMessage = 'Connecting to card reader'

      // verifone_p raises the screen itself via early progress, but the flag also
      // covers pre-validation failures that return before any, and freezes the cart.
      this.store.beginCardReaderTransaction()
      const currencyCode = this.cartTotal.currency_code
      this.armCardReaderWatchdog(VERIFONE_P_NAMESPACE, currencyCode)

      VerifoneP.startTransaction(cardReaderTransactionRequest)
        .then(() => {
          this.store.customLogger('info', 'VerifoneP transaction started', this.store.paymentResponse)
        })
        .catch((error) => {
          // Never taken by the reader, so show the failure rather than dropping the
          // customer back on the catalog with nothing said
          this.failCardReaderTransaction(VERIFONE_P_NAMESPACE, currencyCode, 'Failed to connect to the card reader')
          this.store.customLogger('error', 'Error Starting VerifoneP Transaction', { error: error, paymentResponse: this.store.paymentResponse })
          Sentry.captureException(error, {extra: "startVerifonePPayment: Error connecting to card reader"})
        })
    },
    /**
     * Cancel a verifone P payment request
     * @return {Promise<void>}
     */
    async cancelVerifonePPayment() {
      await VerifoneP.cancelTransaction()
        .catch((error) => {
          Sentry.captureException(error, { extra: "cancelVerifonePPayment: Error canceling VerifoneP transaction" })
        })
    },
    async setupCardReaderListeners() {
      if (!this.kiosk?.cardReaderPaymentMethodInstance) {
        return
      }

      const namespace = this.kiosk.cardReaderPaymentMethodInstance.namespace

      switch (namespace) {
        case VERIFONE_NAMESPACE:
          this.cardReaderMessageListener = await VerifoneVx.addListener('updatedVerifoneMessage', (message) => {
            this.logVerifoneResponse(message)
            this.store.customLogger('info', 'Updated Verifone Message Received', message)
          })
          break
        case VERIFONE_P_NAMESPACE:
          this.verifonePMessageListener = await VerifoneP.addListener('messageUpdate', (message) => {
            this.handleVerifonePResponse(message)
          })

          this.verifonePCompletionListener = await VerifoneP.addListener('paymentCompleted', (data) => {
            this.handleVerifonePCompletion(data)
          })
          break

        default:
          console.log('NO CARD READER PAYMENT METHOD INSTANCE CONFIGURED')
          break
      }
    },
    cleanupCardReaderListeners() {
      if (this.cardReaderMessageListener) {
        this.cardReaderMessageListener.remove()
        this.cardReaderMessageListener = null
      }
      if (this.verifonePMessageListener) {
        this.verifonePMessageListener.remove()
        this.verifonePMessageListener = null
      }
      if (this.verifonePCompletionListener) {
        this.verifonePCompletionListener.remove()
        this.verifonePCompletionListener = null
      }
    },
  },
  watch: {
    'kiosk.cardReaderPaymentMethodInstance': {
      handler(newValue, oldValue) {
        if (newValue && !oldValue) {
          this.setupCardReaderListeners()
        }
      },
      immediate: false,
    },
    selectedLocation(value) {
      let primaryColor = null
      let accentColor = null

      // Set color values based on location
      // Setting primary color
      primaryColor = value?.primaryColor || this.store?.kiosk?.primaryColor || this.store?.kiosk?.organization?.primary_color
      // Settin accent color
      accentColor = value?.accentColor || this.store?.kiosk?.accentColor || this.store?.kiosk?.organization?.accent_color

      if (primaryColor) {
        this.$vuetify.theme.themes.light.primary = primaryColor
        this.$vuetify.theme.themes.light.colors.primary = primaryColor

        this.$vuetify.theme.themes.dark.primary = primaryColor
        this.$vuetify.theme.themes.dark.colors.primary = primaryColor
      }

      if (accentColor) {
        this.$vuetify.theme.themes.light.accent = accentColor
        this.$vuetify.theme.themes.light.colors.accent = accentColor

        this.$vuetify.theme.themes.dark.accent = accentColor
        this.$vuetify.theme.themes.dark.colors.accent = accentColor
      }
    },
    paymentResponse(currentValue) {
      // The reader has spoken, so the connectivity watchdog has nothing left to catch
      if (currentValue?.paymentTransaction) {
        this.clearCardReaderWatchdog()
      }

      if (typeof currentValue !== 'undefined' && currentValue !== null) {
        if (typeof currentValue.paymentTransaction !== 'undefined' && currentValue.paymentTransaction !== null) {
          if (typeof currentValue.paymentTransaction.status !== 'undefined' && currentValue.paymentTransaction.status !== null) {
            if (currentValue.paymentTransaction.status === 'success') {
              try {
                this.store.printReceipt(currentValue)
              } catch (e) {
                Sentry.captureException(e, { extra: "paymentResponse"})
              }
            } else if (currentValue.paymentTransaction.status === 'pending' && currentValue.paymentTransaction.namespace !== VERIFONE_NAMESPACE && currentValue.paymentTransaction.namespace !== VERIFONE_P_NAMESPACE) {
              const transactionUUID = currentValue.paymentTransaction.uuid
              const paymentNamespace = currentValue.paymentTransaction.namespace
              const paymentRequest = this.store.getPaymentRequest(null, this.store.getCartTotal)

              if (currentValue.orderSequence) {
                this.order.sequence = currentValue.orderSequence
              }

              if (paymentRequest?.url) {
                this.store.checkPendingPayment(`${paymentRequest.url}/${transactionUUID}`, paymentNamespace)
              }
            }
            this.store.showPaymentMethodInput = false
          }
        }
      }
    },
    checkoutScreensShowing(value) {
      if (!value) {
        this.store.checkoutInProgress = false
      }
    },
    'store.startPaymentDirectly'(value) {
      if (value) {
        // Reset the flag immediately
        this.store.startPaymentDirectly = false
        // Start the payment
        this.startPayment()
      }
    },
  },
  async mounted() {
    await this.setupCardReaderListeners()
  },
  beforeUnmount() {
    this.clearCardReaderWatchdog()
    this.cleanupCardReaderListeners()
  },
}
</script>
